/**
 * Recent Spots + Nearby Helpers (pure module, zero imports)
 *
 * Backs the location picker's HOME view:
 * - Recents: last 4 visited spots, persisted to localStorage
 * - Nearby: haversine distance helpers for "Spots near me"
 *
 * Dependency-free on purpose (mirrors spot-normalize.js) so it is testable
 * in Node without DOM/browser stubs. All storage access is guarded:
 * private-mode/quota/parse errors degrade silently to "no recents".
 */

const STORAGE_KEY = 'surf-recent-spots';
const MAX_RECENT_SPOTS = 4;
const EARTH_RADIUS_MILES = 3958.8;

/**
 * Read the recent spots list from localStorage
 * Malformed/missing data always degrades to an empty array.
 * @returns {Array<{slug: string, name: string, region: string}>} Most recent first, max 4
 */
export function getRecentSpots() {
    try {
        const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
        if (!raw) return [];

        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];

        return parsed
            .filter(entry => entry &&
                typeof entry.slug === 'string' && entry.slug.length > 0 &&
                typeof entry.name === 'string' && entry.name.length > 0)
            .slice(0, MAX_RECENT_SPOTS)
            .map(entry => ({
                slug: entry.slug,
                name: entry.name,
                region: typeof entry.region === 'string' ? entry.region : ''
            }));
    } catch {
        // Storage unavailable (private mode) or corrupt JSON — no recents
        return [];
    }
}

/**
 * Record a spot visit: de-dupes by slug (move-to-front), caps the list at 4
 * @param {{slug: string, name: string, region: string}} spot - Spot to record
 */
export function recordRecentSpot(spot) {
    if (!spot || typeof spot.slug !== 'string' || spot.slug.length === 0) return;

    try {
        const entry = {
            slug: spot.slug,
            name: typeof spot.name === 'string' && spot.name.length > 0 ? spot.name : spot.slug,
            region: typeof spot.region === 'string' ? spot.region : ''
        };

        const next = [
            entry,
            ...getRecentSpots().filter(existing => existing.slug !== entry.slug)
        ].slice(0, MAX_RECENT_SPOTS);

        globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
        // Storage write failed (private mode, quota) — recents just won't persist
    }
}

/**
 * Great-circle distance between two coordinates in miles (haversine)
 * @param {number} lat1 - Latitude of point 1 (degrees)
 * @param {number} lon1 - Longitude of point 1 (degrees)
 * @param {number} lat2 - Latitude of point 2 (degrees)
 * @param {number} lon2 - Longitude of point 2 (degrees)
 * @returns {number} Distance in miles
 */
export function haversineMiles(lat1, lon1, lat2, lon2) {
    const toRad = (deg) => (deg * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);

    const a = Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;

    // Clamp guards against floating-point drift pushing sqrt input past 1
    return 2 * EARTH_RADIUS_MILES * Math.asin(Math.sqrt(Math.min(1, a)));
}

/**
 * Find the n spots closest to a coordinate
 * Spots without finite lat/lon are skipped.
 * @param {number} lat - Origin latitude (degrees)
 * @param {number} lon - Origin longitude (degrees)
 * @param {Array<Object>} spots - Spot objects with lat/lon fields
 * @param {number} n - Max results (default 5)
 * @returns {Array<{spot: Object, miles: number}>} Sorted nearest-first
 */
export function closestSpots(lat, lon, spots, n = 5) {
    return (spots || [])
        .filter(spot => spot && Number.isFinite(spot.lat) && Number.isFinite(spot.lon))
        .map(spot => ({ spot, miles: haversineMiles(lat, lon, spot.lat, spot.lon) }))
        .sort((a, b) => a.miles - b.miles)
        .slice(0, n);
}

/**
 * Format a distance for the picker's meta chip ("8.4 mi", "12 mi")
 * Under 10 miles shows 1 decimal; 10 and over rounds to an integer.
 * @param {number} miles - Distance in miles
 * @returns {string} Formatted distance, or '' for non-finite input
 */
export function formatMiles(miles) {
    if (!Number.isFinite(miles)) return '';
    return miles < 10 ? `${miles.toFixed(1)} mi` : `${Math.round(miles)} mi`;
}
